import { useEffect, useState, useMemo } from 'react'
import { Upload, Save, FlaskConical, Palette, Plus, Building2, Mail, FileText, Eye, LifeBuoy, Globe, Wand2, X, LayoutDashboard, ChevronRight } from 'lucide-react'
import { supabase, isDemoMode, uploadFile } from '../lib/supabase'
import { applyBrand, loadBrand, DEFAULT_BRAND } from '../lib/brand'
import { DemoNotice } from '../lib/demo'
import SavedIndicator from '../components/SavedIndicator'
import { buildContext, render } from '../lib/orderTemplates'

const SAMPLE_ORDER = {
  reference: 'WG-2026-0042',
  placed_at: new Date().toISOString(),
  placed_by: 'staff@example.com',
  delivery_address: 'Sample Site, Sample Street, Sample Town',
  delivery_postcode: 'AB1 2CD',
  delivery_date_requested: '2026-06-01',
  customer_po: 'PO-1234',
  internal_notes: 'Internal note example',
  client_notes: 'Please leave delivery at reception',
  public_token: 'sample-preview-token',
}
const SAMPLE_COMPANY = {
  name: 'Example Holiday Park',
  contact_name: 'Site Manager',
  contact_email: 'manager@example.com',
}
const SAMPLE_LINES = [
  // Consignment 1 — for John Smith, plot 42
  { line_number: 1, consignment_id: 'sample-c1', recipient_name: 'John Smith', recipient_ref: '42',
    name_at_order: 'Welcome Hamper', sku_at_order: 'AW/HMP/01', unit_at_order: 'box', pack_size_at_order: 1, qty: 2, price_at_order: 78.55, description_at_order: 'Luxury welcome hamper for new owners.' },
  { line_number: 2, consignment_id: 'sample-c1', recipient_name: 'John Smith', recipient_ref: '42',
    name_at_order: 'Dog Welcome Pack', sku_at_order: 'AW/DOG/01', unit_at_order: 'each', pack_size_at_order: 1, qty: 1, price_at_order: 14.95 },
  // Consignment 2 — for Sarah Lee, plot 18
  { line_number: 3, consignment_id: 'sample-c2', recipient_name: 'Sarah Lee', recipient_ref: '18',
    name_at_order: 'Welcome Hamper', sku_at_order: 'AW/HMP/01', unit_at_order: 'box', pack_size_at_order: 1, qty: 1, price_at_order: 78.55 },
]

const TEMPLATE_KINDS = [
  { key: 'client_email',   label: 'Client email',   icon: Mail,     hasSubject: true  },
  { key: 'internal_email', label: 'Internal email', icon: Building2, hasSubject: true  },
  { key: 'client_form',    label: 'Client form',    icon: FileText, hasSubject: false },
]

// WG fallback contacts, used when the current profile has no support_email /
// support_phone set. Update here and every subsequent "Load default template"
// click reflects it.
const WG_FALLBACK_EMAIL = 'orders@waltergeering.co.uk'
const WG_FALLBACK_PHONE = '0330 822 8392'

// Default "How to Order" content used by the Load default button. Contact
// details are interpolated from the current brand profile's support_email /
// support_phone — falling back to WG's own if the client's aren't set.
// Replace every email address and every phone-shaped string in an HTML blob
// with the current profile's support values. Preserves ALL surrounding
// wording, headings, punctuation — only swaps the contact tokens themselves.
// Handles both href="mailto:X" attributes and plain-text occurrences.
function syncContactsInHtml(html, { supportEmail, supportPhone }) {
  if (!html) return html
  let out = html
  const email = (supportEmail || WG_FALLBACK_EMAIL).trim()
  const phone = (supportPhone || WG_FALLBACK_PHONE).trim()
  // Email — hrefs first, then bare text
  out = out.replace(/mailto:([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, `mailto:${email}`)
  out = out.replace(/\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g, email)
  // Phone — UK-shaped: optional + or leading 0, then digits with optional spaces
  out = out.replace(/(?:\+44\s?|0)(?:\d[\s-]?){9,11}\d/g, phone)
  return out
}

function buildDefaultClientHelpHtml({ supportEmail, supportPhone }) {
  const email = (supportEmail || WG_FALLBACK_EMAIL).trim()
  const phone = (supportPhone || WG_FALLBACK_PHONE).trim()
  const isWG = email === WG_FALLBACK_EMAIL
  // "us"/"the team" for object position (email/call THEM), "we"/"the team"
  // for subject position (THEY can do X). Splitting these avoids awkward
  // phrasings like "us can add it for you" that fell out of a single token.
  const usOrTeam = isWG ? 'us' : 'the team'
  const weOrTeam = isWG ? 'we' : 'the team'
  return `<h3>Placing an order</h3>
<p>Placing an order in the portal takes about a minute. Here's the four-step wizard you'll follow — you can go back and change anything at any point.</p>
<ol>
  <li><strong>Step 1 — Choose how to start.</strong> Click <strong>Place Order</strong> in the sidebar. You can either pick a product card (a curated set of items) or duplicate a previous order to save time when you're ordering the same set regularly.</li>
  <li><strong>Step 2 — Add your products.</strong> Set the quantities using the + and − buttons next to each product. If your ordering flow uses <em>consignments</em> (one order split across several recipients), fill in <em>Hamper for?</em> and <em>No.</em> at the top of each consignment, then use <strong>+ New consignment</strong> to add more.</li>
  <li><strong>Step 3 — Delivery details.</strong> Enter your <em>Customer PO</em> (required — this is your internal reference number), pick a requested delivery date, and add any notes for the team.</li>
  <li><strong>Step 4 — Review &amp; send.</strong> Check everything's right, then click <strong>Send order</strong>. You'll see a confirmation on-screen and receive a copy by email straight away.</li>
</ol>

<h3>What happens next</h3>
<p>Your order appears on <strong>My Orders</strong> immediately with the reference number. The Walter Geering team is notified automatically and will be in touch by email or phone if anything's unclear or needs confirming.</p>
<p>Typical turnaround is 3–5 working days from order to delivery, depending on the products and your requested date. You'll get updates by email as the order moves through packing and despatch.</p>

<h3>Frequently asked questions</h3>

<p><strong>Can I save an order to finish later?</strong><br/>
Yes — on Step 3, click <em>Save as draft</em>. The order stays on your <strong>My Orders</strong> page marked as <em>draft</em> until you come back and submit it.</p>

<p><strong>I made a mistake after submitting — what do I do?</strong><br/>
Email or call ${usOrTeam} straight away (details below). We can usually amend an order right up until it's packed. Once it's on the van it's harder to change, so speed matters.</p>

<p><strong>How do I reorder the same thing I ordered last month?</strong><br/>
On Step 1 of Place Order, under <em>Or duplicate a previous order</em>, click any past order. All the same products, quantities and recipients drop straight into the new order — you just need a fresh PO number.</p>

<p><strong>Where can I see my order history?</strong><br/>
Click <strong>My Orders</strong> in the sidebar. Every order is listed with its date, PO, status and reference. Click any order to see the full details, and use the <em>Reorder</em> button on placed orders to duplicate one.</p>

<p><strong>Can I see how much we've spent this quarter?</strong><br/>
Yes — click <strong>Reports</strong> in the sidebar. Pick a date range and you'll see total spend, order count, product breakdown, and a per-site rollup if your account covers multiple sites. You can download the report as a PDF to forward to head office.</p>

<p><strong>Why is a product I usually order missing?</strong><br/>
Product ranges are set per site by your account manager. If something you need isn't showing, ${weOrTeam} can add it for you — just get in touch.</p>

<p><strong>Do I need to enter a PO number every time?</strong><br/>
Yes. It's the reference we quote back on invoices and packing slips, so it's how you match our paperwork to your own. If you don't use POs, any short identifier works (e.g. a date or your initials).</p>

<h3>For head-office users — controlling and approving orders</h3>
<p>If you run a group of sites and want to see every order before it's sent to Walter Geering, you can turn on <strong>head-office approval</strong>. It's off by default.</p>
<ol>
  <li><strong>Turn it on.</strong> In <em>Admin → Client groups → [your group]</em>, tick <em>"Require head-office approval before Walter Geering receives the order"</em>. Enter the approver's name and email underneath, then save.</li>
  <li><strong>What the site sees.</strong> When a site places an order under an approval-required group, they get a confirmation saying <em>"Sent to your head office for approval — you'll be notified when it's approved."</em> The order shows as <strong>Awaiting approval</strong> on their My Orders page.</li>
  <li><strong>What the approver gets.</strong> A single-use email lands in the approver's inbox with a <strong>Review &amp; approve</strong> button. No login required. The link shows the full order — lines, quantities, delivery address, PO — with <strong>Approve</strong> and <strong>Reject</strong> buttons.</li>
  <li><strong>Approve.</strong> The order flips to <em>sent</em>, Walter Geering is notified automatically, and it enters the normal fulfilment flow.</li>
  <li><strong>Reject with a reason.</strong> The order is marked <em>Rejected</em>. The site sees the reason in My Orders and can revise and resubmit.</li>
</ol>
<p>Each approval link is valid for <strong>7 days</strong> and works only once. If it expires, the site just resubmits and a fresh link goes out. You can change the approver email or turn approval off at any time.</p>

<h3>Who to contact</h3>
<p>For anything you can't see in the app, please email
<a href="mailto:${email}">${email}</a>
or call ${usOrTeam} on <strong>${phone}</strong>. We're happy to help.</p>`
}

// Renders a mini mockup of the client portal (login screen + signed-in
// sidebar/dashboard) using the CURRENTLY-BEING-EDITED brand form values.
// No save required. Isolated colours — doesn't affect the surrounding page.
function BrandPreviewModal({ form, onClose }) {
  const primary = form.primary_color || '#76B39D'
  const dark = form.primary_color_dark || '#467D69'
  const light = form.primary_color_light || '#B8D7CC'
  const textColor = form.text_color || '#ffffff'
  const buttonTextColor = form.button_text_color || form.text_color || '#ffffff'
  const sidebarHoverColor = form.sidebar_hover_color || '#ffffff'
  // Convert hex → "r,g,b" for rgba()
  const hexToRgbCsv = (hex) => {
    const h = (hex || '#ffffff').replace('#', '')
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h
    return `${parseInt(n.slice(0,2),16)}, ${parseInt(n.slice(2,4),16)}, ${parseInt(n.slice(4,6),16)}`
  }
  const sidebarHoverRgba = (a) => `rgba(${hexToRgbCsv(sidebarHoverColor)}, ${a})`
  const name = form.name || 'Client Name'
  const logo = form.logo_url
  const logoOnWhite = !!form.logo_on_white
  const bg = form.login_bg_url

  const loginBgStyle = bg
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.55)), url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: `linear-gradient(135deg, ${light}80 0%, ${primary}25 100%)` }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between z-10">
          <div>
            <h3 className="font-semibold text-gray-800">Brand preview</h3>
            <p className="text-xs text-gray-500">Live mockup with your current edits — nothing saved yet.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Login screen mockup */}
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">Login screen</div>
            <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: 340 }}>
              <div className="w-full h-full flex items-center justify-center p-4" style={loginBgStyle}>
                <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-xs">
                  <div className="text-center mb-4">
                    {logo
                      ? <img src={logo} alt="" className="mx-auto mb-2 block" style={{ maxWidth: 140, height: 'auto' }} />
                      : <div className="text-xl font-semibold mb-1" style={{ color: dark }}>{name}</div>
                    }
                    <p className="text-xs text-gray-500">Order Platform</p>
                  </div>
                  <input type="text" placeholder="Email" readOnly className="w-full mb-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm" />
                  <input type="password" placeholder="Password" readOnly className="w-full mb-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm" />
                  <button className="w-full py-2 rounded-lg font-semibold text-sm" style={{ backgroundColor: primary, color: buttonTextColor }}>Sign In</button>
                </div>
              </div>
            </div>
          </div>

          {/* Signed-in mockup: sidebar + main pane */}
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">Signed-in view (client)</div>
            <div className="rounded-xl overflow-hidden border border-gray-200 flex bg-gray-50" style={{ height: 360 }}>
              {/* Sidebar */}
              <div className="w-52 shrink-0 flex flex-col text-sm" style={{ backgroundColor: dark, color: textColor }}>
                <div className="px-4 py-4 border-b border-white/10">
                  {logo
                    ? <img
                        src={logo}
                        alt=""
                        style={{
                          maxWidth: 140, height: 'auto', display: 'block',
                          mixBlendMode: logoOnWhite ? 'multiply' : 'normal',
                        }}
                      />
                    : <div className="font-semibold text-base">{name}</div>
                  }
                  <p className="text-[10px] opacity-70 mt-1">Order Platform</p>
                </div>
                <nav className="flex-1 px-2 py-3 space-y-1">
                  {[
                    { label: 'Place Order', active: true },
                    { label: 'Products' },
                    { label: 'My Orders' },
                    { label: 'Activity' },
                    { label: 'How to Order' },
                  ].map(item => (
                    <div
                      key={item.label}
                      className={`px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${item.active ? 'font-medium' : 'opacity-80'}`}
                      style={item.active ? { backgroundColor: sidebarHoverRgba(0.22) } : undefined}
                    >
                      <div className="w-3.5 h-3.5 rounded bg-white/40" />
                      {item.label}
                      {item.active && <ChevronRight className="w-3 h-3 ml-auto" />}
                    </div>
                  ))}
                </nav>
                <div className="px-2 py-3 border-t border-white/10 opacity-80 text-xs px-4">
                  <div className="font-medium">User Name</div>
                  <div className="text-[10px] opacity-60">user@{(name || 'client').toLowerCase().replace(/\s+/g,'')}.com</div>
                </div>
              </div>
              {/* Main pane */}
              <div className="flex-1 flex flex-col">
                <div className="bg-white border-b border-gray-200 px-6 py-3">
                  <div className="text-xs text-gray-500">{name}</div>
                  <div className="text-sm font-semibold text-gray-800">Place Order</div>
                </div>
                <div className="flex-1 p-6 overflow-auto">
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Welcome Hamper</div>
                        <div className="text-xs text-gray-500">£45.00 · Ideal for new arrivals</div>
                      </div>
                      <button className="text-xs px-3 py-1.5 rounded font-medium" style={{ backgroundColor: primary, color: buttonTextColor }}>Add to order</button>
                    </div>
                  </div>
                  <div className="rounded-lg border p-4" style={{ borderColor: light, backgroundColor: light + '30' }}>
                    <div className="text-xs" style={{ color: dark }}>Consignment · Hamper for <strong>Sarah Lee</strong> · No. <strong>42</strong></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colour swatches for reference */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <div className="text-xs uppercase tracking-wide text-gray-500 font-medium mr-2">Palette</div>
            {[
              { hex: primary, label: 'Primary' },
              { hex: dark, label: 'Dark' },
              { hex: light, label: 'Light' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded border border-gray-200" style={{ backgroundColor: s.hex }} />
                <div className="text-xs text-gray-600"><span className="font-medium">{s.label}</span> <span className="font-mono text-gray-400">{s.hex}</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Settings({ brand, setBrand }) {
  const [profiles, setProfiles] = useState([])     // all brand_settings rows
  const [selectedId, setSelectedId] = useState(null)
  const [form, setForm] = useState(brand || {})
  const [companies, setCompanies] = useState([])   // all companies
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(0)
  const [uploading, setUploading] = useState(false)
  // Template state
  const [templates, setTemplates] = useState([])
  const [activeKind, setActiveKind] = useState('client_email')
  const [templateDraft, setTemplateDraft] = useState({ subject: '', body_html: '' })
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateSavedAt, setTemplateSavedAt] = useState(0)
  // Brand-from-URL scraper state
  const [scrapeUrl, setScrapeUrl] = useState('')
  const [scraping, setScraping] = useState(false)
  const [scrapeError, setScrapeError] = useState('')
  const [scrapeResult, setScrapeResult] = useState(null) // { name, description, candidates: [], colours: [], fonts: [] }
  const [pickingLogo, setPickingLogo] = useState(false)
  // Preview modal state
  const [showPreview, setShowPreview] = useState(false)

  // ---- Load profiles + companies
  const reload = async () => {
    if (isDemoMode) return
    const [pRes, cRes, tRes] = await Promise.all([
      supabase.from('brand_settings').select('*').order('scope').order('name'),
      supabase.from('companies').select('id, name, brand_settings_id').is('archived_at', null).order('name'),
      supabase.from('order_templates').select('*').eq('scope', 'global'),
    ])
    setProfiles(pRes.data || [])
    setCompanies(cRes.data || [])
    setTemplates(tRes.data || [])
    // Default to the currently-active brand profile.
    if (!selectedId) {
      const def = (pRes.data || []).find(p => p.id === brand?.id) || (pRes.data || []).find(p => p.scope === 'global')
      if (def) {
        setSelectedId(def.id)
        setForm(def)
      }
    }
  }
  useEffect(() => { reload() }, [])

  // Editing a profile live-applies its colours via the set() helper so the
  // user gets WYSIWYG feedback. If they navigate away without saving, the
  // page keeps those preview colours until the next full reload. Restore
  // the true active brand on unmount so the sidebar/theme snaps back.
  useEffect(() => {
    return () => {
      loadBrand().then(b => { if (b) applyBrand(b) }).catch(() => {})
    }
  }, [])

  // ---- When the user picks a template kind, populate the editor draft.
  useEffect(() => {
    const row = templates.find(t => t.kind === activeKind)
    setTemplateDraft({ subject: row?.subject || '', body_html: row?.body_html || '' })
  }, [activeKind, templates])

  // ---- Render the preview using the current draft + sample data + current brand.
  const renderedPreview = useMemo(() => {
    const ctxBase = buildContext({
      order: SAMPLE_ORDER, company: SAMPLE_COMPANY, brand: form, lines: SAMPLE_LINES, formHtml: '',
    })
    const formTpl = templates.find(t => t.kind === 'client_form')?.body_html || ''
    const formHtml = activeKind === 'client_form'
      ? render(templateDraft.body_html, ctxBase)
      : render(formTpl, ctxBase)
    const ctx = { ...ctxBase, formHtml }
    return {
      subject: render(templateDraft.subject || '', ctx),
      body: render(templateDraft.body_html, ctx),
    }
  }, [activeKind, templateDraft, templates, form])

  const saveTemplate = async () => {
    if (isDemoMode) return
    setTemplateSaving(true)
    try {
      const existing = templates.find(t => t.kind === activeKind && t.scope === 'global')
      const payload = {
        scope: 'global',
        kind: activeKind,
        subject: TEMPLATE_KINDS.find(k => k.key === activeKind)?.hasSubject ? (templateDraft.subject || null) : null,
        body_html: templateDraft.body_html || '',
      }
      const { error } = existing
        ? await supabase.from('order_templates').update(payload).eq('id', existing.id)
        : await supabase.from('order_templates').insert(payload)
      if (error) throw error
      await reload()
      setTemplateSavedAt(Date.now())
    } catch (err) {
      alert(err.message)
    } finally {
      setTemplateSaving(false)
    }
  }

  // ---- Switch selection -> load that profile into the form
  useEffect(() => {
    if (!selectedId) return
    const p = profiles.find(x => x.id === selectedId)
    if (p) {
      setForm(p)
      applyBrand(p) // live preview the picked profile in this admin UI
    }
  }, [selectedId])

  const isGlobal = form?.scope === 'global'
  const linkedCompanyIds = useMemo(
    () => companies.filter(c => c.brand_settings_id === selectedId).map(c => c.id),
    [companies, selectedId]
  )

  const set = (key, value) => {
    const next = { ...form, [key]: value }
    setForm(next)
    applyBrand(next)
  }

  const onLogo = async (e) => {
    const file = e.target.files?.[0]
    if (!file || isDemoMode) return
    setUploading(true)
    try {
      const { url } = await uploadFile(file, 'brand-assets')
      set('logo_url', url)
    } catch (err) {
      alert(err.message)
    } finally {
      setUploading(false)
    }
  }

  const onLoginBg = async (e) => {
    const file = e.target.files?.[0]
    if (!file || isDemoMode) return
    setUploading(true)
    try {
      const { url } = await uploadFile(file, 'brand-assets')
      set('login_bg_url', url)
    } catch (err) {
      alert(err.message)
    } finally {
      setUploading(false)
    }
  }

  // Fetch a URL via the scrape-brand Edge Function and show candidate logos.
  const scrapeBrand = async () => {
    const url = scrapeUrl.trim()
    if (!url || isDemoMode) return
    setScraping(true)
    setScrapeError('')
    setScrapeResult(null)
    try {
      const { data, error } = await supabase.functions.invoke('scrape-brand', { body: { url } })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Fetch failed')
      setScrapeResult(data)
    } catch (err) {
      setScrapeError(err.message || 'Fetch failed')
    } finally {
      setScraping(false)
    }
  }

  // User picked one of the scraped candidates. We route the download through
  // the scrape-brand Edge Function (image_url mode) rather than fetching from
  // the browser — that bypasses cross-origin CORS blocks (most sites, including
  // Marston's, refuse direct browser fetches of their images). The function
  // returns a base64 data URI which we turn into a Blob and upload to our
  // own Supabase Storage so it doesn't 404 later.
  const pickLogoCandidate = async (candidate) => {
    if (isDemoMode) return
    setPickingLogo(true)
    try {
      const { data, error } = await supabase.functions.invoke('scrape-brand', {
        body: { image_url: candidate.url },
      })
      if (error) throw error
      if (!data?.ok || !data.data_uri) throw new Error(data?.error || 'Fetch failed')
      // data_uri → Blob via fetch (browsers accept data: URIs natively).
      const blobRes = await fetch(data.data_uri)
      const blob = await blobRes.blob()
      const extFromType = (data.content_type || '').split('/')[1] || ''
      const extFromUrl = candidate.url.split(/[?#]/)[0].split('.').pop() || ''
      const ext = (extFromType || extFromUrl || 'png').toLowerCase().slice(0, 5)
      const filename = `scraped-${Date.now()}.${ext}`
      const file = new File([blob], filename, { type: data.content_type || blob.type || 'image/png' })
      const { url } = await uploadFile(file, 'brand-assets')
      set('logo_url', url)
      if (scrapeResult?.name && (!form.name || form.name === '(unnamed)')) {
        set('name', scrapeResult.name)
      }
      setScrapeResult(null)
      setScrapeUrl('')
    } catch (err) {
      alert(`Couldn't fetch that image: ${err.message || err}. Try another candidate, or save it manually and upload.`)
    } finally {
      setPickingLogo(false)
    }
  }

  const saveProfile = async () => {
    if (isDemoMode) return
    setSaving(true)
    try {
      const { id, created_at, updated_at, ...payload } = form
      const { error } = await supabase.from('brand_settings').update(payload).eq('id', id)
      if (error) throw error
      // Refresh the app's active brand if we just edited it.
      if (id === brand?.id) {
        const fresh = await loadBrand(brand.company_id || null)
        setBrand(fresh)
        applyBrand(fresh)
      }
      await reload()
      setSavedAt(Date.now())
    } catch (err) {
      alert(err.message)
    } finally {
      setSaving(false)
    }
  }

  const createProfile = async () => {
    if (isDemoMode) return
    const name = prompt('Name for the new brand profile (e.g. the client\'s name):')
    if (!name) return
    const { data, error } = await supabase.from('brand_settings').insert({
      scope: 'company',
      name,
      primary_color: '#76B39D',
      primary_color_dark: '#467D69',
      primary_color_light: '#B8D7CC',
    }).select().single()
    if (error) return alert(error.message)
    await reload()
    setSelectedId(data.id)
  }

  const toggleCompany = async (companyId, on) => {
    if (isDemoMode) return
    await supabase.from('companies').update({
      brand_settings_id: on ? selectedId : null,
    }).eq('id', companyId)
    await reload()
  }

  if (profiles.length === 0 && !isDemoMode) {
    return <div className="card"><p className="text-sm text-gray-500">Loading brand profiles…</p></div>
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <DemoNotice />

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Palette className="w-5 h-5 text-brand" /> Brand profiles
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          The <strong>Global</strong> profile is what WG staff and the login screen show. Each client
          gets its own profile — link companies to it below so their portal users see their own colours
          and logo.
        </p>

        <div className="flex gap-2 mb-4">
          <select
            value={selectedId || ''}
            onChange={e => setSelectedId(e.target.value)}
            className="input-field flex-1 max-w-md"
          >
            {profiles.map(p => (
              <option key={p.id} value={p.id}>
                {p.scope === 'global' ? '[Global] ' : ''}{p.name || '(unnamed)'}
              </option>
            ))}
          </select>
          <button onClick={createProfile} className="btn-secondary text-sm">
            <Plus className="w-4 h-4" /> New profile
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Profile name</span>
            <input type="text" value={form.name || ''} onChange={e => set('name', e.target.value)} className="input-field" />
          </label>

          <div className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Logo</span>
            <div className="flex items-center gap-4">
              <div
                className="w-40 h-20 rounded-lg flex items-center justify-center p-2 transition-colors"
                style={{
                  backgroundColor: `rgb(${(form.primary_color_dark || '#467D69').replace('#','').match(/.{2}/g).map(x => parseInt(x, 16)).join(' ')})`,
                }}
              >
                {form.logo_url
                  ? <img
                      src={form.logo_url}
                      alt=""
                      className="max-h-full max-w-full"
                      style={{ mixBlendMode: form.logo_on_white ? 'multiply' : 'normal' }}
                    />
                  : <span className="text-white/60 text-xs">No logo</span>
                }
              </div>
              <label className="btn-secondary text-sm cursor-pointer">
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading…' : 'Upload logo'}
                <input type="file" accept="image/*" onChange={onLogo} className="hidden" />
              </label>
              {form.logo_url && (
                <button onClick={() => set('logo_url', null)} className="text-sm text-gray-500 hover:text-red-600">
                  Remove
                </button>
              )}
            </div>
            <label className="mt-2 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.logo_on_white}
                onChange={e => set('logo_on_white', e.target.checked)}
                className="mt-1 w-4 h-4"
              />
              <span className="text-xs text-gray-600">
                <span className="font-medium text-gray-800">Logo has a white background — blend it out</span>
                <span className="block text-gray-500 mt-0.5">
                  Tick this if the logo image itself is on a white background
                  (like the Marston's wordmark). We'll blend the white pixels into the
                  sidebar colour so only the dark marks show, without a jarring white box.
                </span>
              </span>
            </label>

            {/* Import from URL — scrape their website for a logo */}
            <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="w-4 h-4 text-brand" />
                <span className="text-sm font-medium text-gray-700">Import from website</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Paste the client's website — we'll grab their logo and brand name automatically.
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="url"
                    value={scrapeUrl}
                    onChange={e => setScrapeUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !scraping && scrapeBrand()}
                    placeholder="marstons.co.uk"
                    className="input-field pl-9"
                  />
                </div>
                <button onClick={scrapeBrand} disabled={scraping || !scrapeUrl.trim()} className="btn-secondary text-sm">
                  {scraping ? 'Fetching…' : 'Fetch'}
                </button>
              </div>
              {scrapeError && <p className="mt-2 text-xs text-red-600">{scrapeError}</p>}

              {scrapeResult && (
                <div className="mt-3 p-3 rounded-lg bg-white border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        Found: {scrapeResult.name || '(no name detected)'}
                      </div>
                      {scrapeResult.description && (
                        <div className="text-xs text-gray-500 truncate">{scrapeResult.description}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">{scrapeResult.candidates?.length || 0} logo candidates — pick one:</div>
                    </div>
                    <button onClick={() => setScrapeResult(null)} className="text-gray-400 hover:text-gray-700 shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {scrapeResult.candidates?.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {scrapeResult.candidates.slice(0, 15).map((c, i) => (
                        <button
                          key={i}
                          onClick={() => pickLogoCandidate(c)}
                          disabled={pickingLogo}
                          title={`${c.kind}${c.size ? ` · ${c.size}` : ''}${c.hint ? ` · ${c.hint}` : ''}`}
                          className="group relative aspect-square rounded-md border border-gray-200 bg-gray-50 hover:border-brand hover:shadow-sm disabled:opacity-50 transition p-1 overflow-hidden"
                        >
                          <img
                            src={c.url}
                            alt={c.kind}
                            className="w-full h-full object-contain"
                            onError={e => { e.currentTarget.parentElement.style.display = 'none' }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 opacity-0 group-hover:opacity-100 transition">
                            {c.kind}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No logo images found on that page. Try a URL deeper into their site (e.g. their About page).</p>
                  )}
                  {pickingLogo && <p className="text-xs text-gray-500 mt-2">Uploading chosen logo…</p>}

                  {/* Colour palette detected from the site */}
                  {scrapeResult.colours?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-2">
                        Detected brand colours — click a swatch to set as <strong>Primary</strong>, hold Shift for <strong>Dark</strong>, hold Alt for <strong>Light</strong>:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {scrapeResult.colours.map((hex, i) => (
                          <button
                            key={i}
                            onClick={(e) => {
                              if (e.shiftKey) set('primary_color_dark', hex)
                              else if (e.altKey) set('primary_color_light', hex)
                              else set('primary_color', hex)
                            }}
                            title={`${hex} — click=primary · shift-click=dark · alt-click=light`}
                            className="w-10 h-10 rounded-md border border-gray-200 hover:scale-110 transition-transform shadow-sm"
                            style={{ backgroundColor: hex }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Google Fonts detected — informational only for now */}
                  {scrapeResult.fonts?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                      Detected fonts (Google Fonts): {scrapeResult.fonts.map((f, i) => (
                        <span key={i} className="inline-block mr-2 px-2 py-0.5 bg-gray-100 rounded text-gray-700 font-medium">{f}</span>
                      ))}
                      <div className="text-gray-400 mt-1 italic">Note: platform fonts stay consistent for readability — this is just a reference for your reports/emails.</div>
                    </div>
                  )}

                  {/* Contacts detected — click to save as the profile's support email / phone */}
                  {(scrapeResult.contacts?.emails?.length > 0 || scrapeResult.contacts?.phones?.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-2">
                        Detected contact details — click one to save as this profile's support contact
                        (used in the client help template):
                      </div>
                      {scrapeResult.contacts?.emails?.length > 0 && (
                        <div className="mb-2">
                          <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Emails</div>
                          <div className="flex flex-wrap gap-1.5">
                            {scrapeResult.contacts.emails.map((e, i) => {
                              const active = form.support_email === e
                              return (
                                <button
                                  key={i}
                                  onClick={() => set('support_email', e)}
                                  title={active ? 'Currently saved as support email' : 'Click to save as support email'}
                                  className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 transition ${
                                    active
                                      ? 'bg-brand text-white border-brand'
                                      : 'bg-white text-gray-700 border-gray-200 hover:border-brand hover:text-brand-dark'
                                  }`}
                                >
                                  {active && '✓ '}{e}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                      {scrapeResult.contacts?.phones?.length > 0 && (
                        <div>
                          <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Phones</div>
                          <div className="flex flex-wrap gap-1.5">
                            {scrapeResult.contacts.phones.map((p, i) => {
                              const active = form.support_phone === p
                              return (
                                <button
                                  key={i}
                                  onClick={() => set('support_phone', p)}
                                  title={active ? 'Currently saved as support phone' : 'Click to save as support phone'}
                                  className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 transition ${
                                    active
                                      ? 'bg-brand text-white border-brand'
                                      : 'bg-white text-gray-700 border-gray-200 hover:border-brand hover:text-brand-dark'
                                  }`}
                                >
                                  {active && '✓ '}{p}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Login background image — full-bleed photo behind the sign-in card */}
          <div className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Login screen background</span>
            <p className="text-xs text-gray-400 mb-2">
              Landscape photo shown behind the sign-in card. Ideal: 1920×1080 or larger, dark or richly-coloured
              so the white card pops.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-40 h-24 rounded-lg bg-gray-800 flex items-center justify-center overflow-hidden">
                {form.login_bg_url
                  ? <img src={form.login_bg_url} alt="" className="w-full h-full object-cover" />
                  : <span className="text-white/60 text-xs">No background</span>
                }
              </div>
              <label className="btn-secondary text-sm cursor-pointer">
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading…' : 'Upload background'}
                <input type="file" accept="image/*" onChange={onLoginBg} className="hidden" />
              </label>
              {form.login_bg_url && (
                <button onClick={() => set('login_bg_url', null)} className="text-sm text-gray-500 hover:text-red-600">
                  Remove
                </button>
              )}
            </div>
          </div>

          <label>
            <span className="text-xs text-gray-500 block mb-1">Primary colour</span>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.primary_color || '#76B39D'} onChange={e => set('primary_color', e.target.value)} className="w-12 h-10 rounded border border-gray-200" />
              <input type="text" value={form.primary_color || ''} onChange={e => set('primary_color', e.target.value)} className="input-field" />
            </div>
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Primary dark (sidebar &amp; hover)</span>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.primary_color_dark || '#467D69'} onChange={e => set('primary_color_dark', e.target.value)} className="w-12 h-10 rounded border border-gray-200" />
              <input type="text" value={form.primary_color_dark || ''} onChange={e => set('primary_color_dark', e.target.value)} className="input-field" />
            </div>
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Primary light (accents)</span>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.primary_color_light || '#B8D7CC'} onChange={e => set('primary_color_light', e.target.value)} className="w-12 h-10 rounded border border-gray-200" />
              <input type="text" value={form.primary_color_light || ''} onChange={e => set('primary_color_light', e.target.value)} className="input-field" />
            </div>
          </label>

          <label>
            <span className="text-xs text-gray-500 block mb-1">Sidebar text colour</span>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.text_color || '#ffffff'} onChange={e => set('text_color', e.target.value)} className="w-12 h-10 rounded border border-gray-200" />
              <input type="text" value={form.text_color || ''} onChange={e => set('text_color', e.target.value)} placeholder="#ffffff" className="input-field" />
            </div>
            <span className="text-[11px] text-gray-500 block mt-1">Text over the sidebar (Primary dark). White for dark sidebars; go dark if you make the sidebar light.</span>
          </label>

          <label>
            <span className="text-xs text-gray-500 block mb-1">Sidebar hover colour</span>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.sidebar_hover_color || '#ffffff'} onChange={e => set('sidebar_hover_color', e.target.value)} className="w-12 h-10 rounded border border-gray-200" />
              <input type="text" value={form.sidebar_hover_color || ''} onChange={e => set('sidebar_hover_color', e.target.value)} placeholder="#ffffff" className="input-field" />
            </div>
            <span className="text-[11px] text-gray-500 block mt-1">Colour that flashes behind nav items on hover and active. Used at low opacity — white works on dark sidebars, black on light sidebars.</span>
          </label>

          <label>
            <span className="text-xs text-gray-500 block mb-1">Button text colour</span>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.button_text_color || '#ffffff'} onChange={e => set('button_text_color', e.target.value)} className="w-12 h-10 rounded border border-gray-200" />
              <input type="text" value={form.button_text_color || ''} onChange={e => set('button_text_color', e.target.value)} placeholder="#ffffff" className="input-field" />
            </div>
            <span className="text-[11px] text-gray-500 block mt-1">Text on primary buttons (Save order, Sign in, etc.), which sit on the Primary colour. Set independently from sidebar text.</span>
          </label>

          <label>
            <span className="text-xs text-gray-500 block mb-1">Support email (shown in client help)</span>
            <input
              type="email"
              value={form.support_email || ''}
              onChange={e => set('support_email', e.target.value)}
              placeholder={`Falls back to ${WG_FALLBACK_EMAIL}`}
              className="input-field"
            />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Support phone (shown in client help)</span>
            <input
              type="tel"
              value={form.support_phone || ''}
              onChange={e => set('support_phone', e.target.value)}
              placeholder={`Falls back to ${WG_FALLBACK_PHONE}`}
              className="input-field"
            />
          </label>

          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Email footer (HTML, appears at the bottom of every order email)</span>
            <textarea
              value={form.email_footer_html || ''}
              onChange={e => set('email_footer_html', e.target.value)}
              className="input-field font-mono text-xs"
              rows={4}
            />
          </label>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button onClick={saveProfile} disabled={saving} className="btn-primary">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save profile'}
          </button>
          <button onClick={() => setShowPreview(true)} className="btn-secondary" title="See how this brand looks in the client portal — no need to save first">
            <Eye className="w-4 h-4" /> Preview
          </button>
          <SavedIndicator savedAt={savedAt} />
        </div>
      </div>

      {showPreview && (
        <BrandPreviewModal form={form} onClose={() => setShowPreview(false)} />
      )}

      {!isGlobal && selectedId && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-brand" /> Linked companies
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            Companies ticked here use the <strong>{form.name || '(unnamed)'}</strong> brand profile.
            Unticked companies fall back to Global.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-auto p-2 bg-gray-50 rounded-lg">
            {companies.map(c => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={linkedCompanyIds.includes(c.id)}
                  onChange={e => toggleCompany(c.id, e.target.checked)}
                />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Mail className="w-5 h-5 text-brand" />
          Email delivery
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Where order emails come from, where client replies go, and who at WG gets the internal "new order" notification.
          Settings on the <strong>{form.scope === 'global' ? 'Global' : form.name || '(unnamed)'}</strong> profile apply to orders for the companies linked to this profile.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">From address (must be a verified Resend sender)</span>
            <input
              type="text"
              value={form.from_email || ''}
              onChange={e => set('from_email', e.target.value)}
              placeholder='e.g. WG Orders &lt;orders@yourdomain.co.uk&gt;'
              className="input-field"
            />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Reply-to address (where client replies land)</span>
            <input
              type="email"
              value={form.reply_to_email || ''}
              onChange={e => set('reply_to_email', e.target.value)}
              placeholder="orders@yourdomain.co.uk"
              className="input-field"
            />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">BCC (optional, every order copied here)</span>
            <input
              type="email"
              value={form.bcc_email || ''}
              onChange={e => set('bcc_email', e.target.value)}
              placeholder="archive@yourdomain.co.uk"
              className="input-field"
            />
          </label>
          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Internal recipients (comma-separated emails who get the "new order" notification)</span>
            <input
              type="text"
              value={form.internal_recipients || ''}
              onChange={e => set('internal_recipients', e.target.value)}
              placeholder="ops@waltergeering.co.uk, sales@waltergeering.co.uk"
              className="input-field"
            />
          </label>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button onClick={saveProfile} disabled={saving} className="btn-primary">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save email settings'}
          </button>
          <SavedIndicator savedAt={savedAt} />
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-brand" />
          Client help &amp; instructions
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Shown to client users on the <em>Help</em> tab in their portal. Plain HTML — use it for "how to place an order", "what happens next", and who to contact.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <label>
            <span className="text-xs text-gray-500 block mb-1">Help content (HTML)</span>
            <textarea
              value={form.client_help_html || ''}
              onChange={e => set('client_help_html', e.target.value)}
              className="input-field font-mono text-xs"
              style={{ minHeight: 320 }}
            />
          </label>
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide block mb-1 flex items-center gap-1">
              <Eye className="w-3 h-3" /> Preview
            </span>
            <div className="border border-gray-200 rounded-lg bg-white p-4 overflow-auto" style={{ minHeight: 320 }}>
              <div className="prose prose-sm max-w-none [&_h3]:text-brand-dark [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_a]:text-brand-dark [&_a]:underline [&_li]:my-1"
                   dangerouslySetInnerHTML={{ __html: form.client_help_html || '<p class="text-gray-400 italic">Nothing here yet.</p>' }} />
            </div>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <button onClick={saveProfile} disabled={saving} className="btn-primary">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save help content'}
          </button>
          <button
            onClick={() => {
              if (form.client_help_html && !confirm('Overwrite the current help content with the default template? Your existing text will be lost.')) return
              set('client_help_html', buildDefaultClientHelpHtml({
                supportEmail: form.support_email,
                supportPhone: form.support_phone,
              }))
            }}
            className="btn-secondary text-sm"
            title="Fill the editor with a ready-to-use template using this profile's support contact (or WG defaults)"
          >
            <Wand2 className="w-4 h-4" /> Load default template
          </button>
          <button
            onClick={() => {
              if (!form.client_help_html) return alert('Load the default template first, then use this to swap in contact details.')
              set('client_help_html', syncContactsInHtml(form.client_help_html, {
                supportEmail: form.support_email,
                supportPhone: form.support_phone,
              }))
            }}
            disabled={!form.client_help_html}
            className="btn-secondary text-sm"
            title="Update every email and phone in the help text to this profile's support contacts — keeps all other wording"
          >
            <Mail className="w-4 h-4" /> Sync contact details
          </button>
          <SavedIndicator savedAt={savedAt} />
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-amber-500" />
          Test mode (for this profile)
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          With test mode <strong>on</strong>, sends made under this profile are diverted to the test email.
          Set this on the Global profile while you're getting set up, then turn it off to go live.
        </p>

        <label className="flex items-start gap-3 mb-4">
          <input
            type="checkbox"
            checked={!!form.test_mode}
            onChange={e => set('test_mode', e.target.checked)}
            className="mt-1 w-4 h-4"
          />
          <span>
            <span className="block font-medium text-gray-800">Test mode is {form.test_mode ? 'ON — nothing goes live' : 'OFF — sends will reach real recipients'}</span>
            <span className="block text-xs text-gray-500 mt-0.5">A "Send test" button is also available on every order regardless of this switch.</span>
          </span>
        </label>

        <label>
          <span className="text-xs text-gray-500 block mb-1">Test email address</span>
          <input
            type="email"
            value={form.test_email || ''}
            onChange={e => set('test_email', e.target.value)}
            placeholder="you@example.com"
            className="input-field max-w-md"
          />
        </label>

        <div className="mt-5 flex items-center gap-3">
          <button onClick={saveProfile} disabled={saving} className="btn-primary">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save test settings'}
          </button>
          <SavedIndicator savedAt={savedAt} />
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <FileText className="w-5 h-5 text-brand" />
          Email templates (global)
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Default layouts for the three things every order produces: the client confirmation email, the internal "new order" email, and the client-facing order form (used inside emails and at <code>/v/:token</code>). Live preview on the right uses sample data with your current brand applied. Per-order overrides happen on the order's preview tab.
        </p>

        <div className="flex gap-1 border-b border-gray-200 mb-3 overflow-x-auto">
          {TEMPLATE_KINDS.map(k => (
            <button
              key={k.key} onClick={() => setActiveKind(k.key)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeKind === k.key
                  ? 'border-brand text-brand-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <k.icon className="w-4 h-4" /> {k.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            {TEMPLATE_KINDS.find(k => k.key === activeKind)?.hasSubject && (
              <label className="block">
                <span className="text-xs text-gray-500 block mb-1">Subject line</span>
                <input
                  type="text"
                  value={templateDraft.subject || ''}
                  onChange={e => setTemplateDraft(d => ({ ...d, subject: e.target.value }))}
                  className="input-field"
                />
              </label>
            )}
            <label className="block">
              <span className="text-xs text-gray-500 block mb-1">Body (HTML)</span>
              <textarea
                value={templateDraft.body_html || ''}
                onChange={e => setTemplateDraft(d => ({ ...d, body_html: e.target.value }))}
                className="input-field font-mono text-xs"
                style={{ minHeight: 360 }}
              />
            </label>
            <p className="text-xs text-gray-400">
              Tokens: <code>{'{{order.reference}}'}</code>, <code>{'{{order.public_url}}'}</code>, <code>{'{{order.total}}'}</code>, <code>{'{{order.delivery_address}}'}</code>, <code>{'{{company.name}}'}</code>, <code>{'{{company.contact_name}}'}</code>, <code>{'{{brand.name}}'}</code>, <code>{'{{lines}}'}</code>, <code>{'{{form}}'}</code>, <code>{'{{brand.email_footer}}'}</code>.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <button onClick={saveTemplate} disabled={templateSaving} className="btn-primary">
                <Save className="w-4 h-4" /> {templateSaving ? 'Saving…' : 'Save template'}
              </button>
              <SavedIndicator savedAt={templateSavedAt} />
            </div>
          </div>

          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide block mb-1 flex items-center gap-1">
              <Eye className="w-3 h-3" /> Preview (sample order)
            </span>
            {renderedPreview.subject && (
              <div className="mb-2 text-sm">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Subject</span>
                <div className="font-medium text-gray-800">{renderedPreview.subject}</div>
              </div>
            )}
            <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" style={{ minHeight: 360 }}>
              <iframe srcDoc={renderedPreview.body} title="template-preview" className="w-full h-full" style={{ minHeight: 360, border: 0 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
